import React, { Component, RefObject } from 'react'

import {
    BACKSCAPE_KEY, DELETE_KEY, DOWN_ARROW_KEY, END_KEY, ENTER_KEY, HOME_KEY, LEFT_ARROW_KEY, PAGE_DOWN_KEY,
    PAGE_UP_KEY, RIGHT_ARROW_KEY, UP_ARROW_KEY
} from 'app/helpers/keymap'

import SVGTextWrapped, {
    getFontMetrics,
    getStringWidthAndHeight,
    TextAnchorType,
} from "../svg-text-utils/SVGTextWrapped";

import ShapeType, { NO_TEXT_CURSOR_TYPES, RECTANGLE_EACH_LINE_TYPE } from "./types/shape";
import { GradientFillUrl } from 'shape-edit/forms/fields';
import { MOVE_CURSOR } from 'template-edit/containers/image-preview';
import { RangeType } from 'shape-edit/types';
import TextCursorSvg from 'shape-entities/components/text-cursor-svg';
import { debounce, escapeRegExp } from 'lodash';
import { checkOverflowAndResizeShape, getAdaptiveFontSize, getTrimmedContent } from 'template-edit/components/helper';
import { EditModeType } from 'app/types/app-types';
import { getFormName, StateType } from 'shape-edit/forms/helpers';
import store from 'store';
import { isJson } from 'app/helpers/other';
import StrokeUnderlayerFilter, { getStrokeUnderlayerFilterId, strokeUnderlayerFilterUrl } from 'svg-text-utils/StrokeUnderlayerFilter';
import { getPangoLineRanges, getPangoMarkupIndex, hasPangoMarkup, renderPangoMarkup, stripPangoMarkup } from 'svg-text-utils/pango-markup';

export const manualBreakLine = "\n";

//fired by Shape.selectAllShapeText (double click while text-editing) - RenderText owns the selection as local
//state, so it can't be set directly from there and listens for this instead
export const SELECT_ALL_TEXT_EVENT = 'shape:select-all-text';

const { min, max, abs, sin, cos, PI, round, atan2, sqrt, ceil } = Math

interface Props {
    shape: ShapeType
    fontSize: number
    width: number
    h: number
    offsetY: number
    activeShapeId: string   //just for componentDidUpdate, do not use for different reason, see getIsActive
    editMode: EditModeType
    setRealSize: Function
    updateShape?: Function
}

interface State {
    cursorPosition: number  //character position (starting from 0) in the text_content
    textSelection: RangeType
    textRanges: RangeType[]
    textRangesWithLeadingSpaces: RangeType[]
    isSelecting: boolean
}

class RenderText extends Component<Props, State> {
    mainText: RefObject<SVGTextElement>
    underlayerFilterId = getStrokeUnderlayerFilterId()
    tspanList: RefObject<SVGTSpanElement>[]
    selectionAnchorPos: number
    keySelectionAnchor: number | null
    mouseDownWasDrag: boolean

    constructor(props: Props) {
        super(props)

        this.state = {
            cursorPosition: 0,
            textSelection: null,
            textRanges: [],
            textRangesWithLeadingSpaces: [],
            isSelecting: false,
        }

        this.mainText = React.createRef()
        this.tspanList = []
        this.selectionAnchorPos = 0
        this.keySelectionAnchor = null
        this.mouseDownWasDrag = false
    }

    //document.addEventListener("mouseover", this.handleMouseOver)
    //document.removeEventListener('mouseover', this.handleMouseOver)
    /* handleMouseOver = (e) => {
        if (this.isActiveAndTextCursorType()) {
            if (e.target instanceof SVGTSpanElement && e.target.parentNode.className.baseVal === 'editable-text-active' && this.textEl !== e.target.parentNode) {
                this.tspanEl = e.target
                this.textEl = e.target.parentNode
            } else if (e.target instanceof SVGTextElement && e.target.className.baseVal === 'editable-text-active' && this.textEl !== e.target) {
                this.tspanEl = null
                this.textEl = e.target
            }
        } else {
            this.textEl = null
            this.tspanEl = null
        }
    } */

    updateTextRangesDebounced = debounce(() => {
        this.updateTextRanges()
    }, 200)

    updateTextRanges = () => {
        this.setState({
            textRanges: this.getTextRanges(true, false),
            textRangesWithLeadingSpaces: this.getTextRanges(true, true)
        })
    }

    componentDidMount(): void {
        if (!NO_TEXT_CURSOR_TYPES.includes(this.props.shape.type) && typeof this.props.updateShape === 'function') {
            document.addEventListener("keydown", this.handleKeyDown)
            document.addEventListener("selectionchange", this.syncCursorFromTextarea)
            document.addEventListener(SELECT_ALL_TEXT_EVENT, this.handleSelectAllTextEvent)
            this.updateTextRanges()
        }
    }

    componentWillUnmount(): void {
        if (!NO_TEXT_CURSOR_TYPES.includes(this.props.shape.type) && typeof this.props.updateShape === 'function') {
            document.removeEventListener('keydown', this.handleKeyDown)
            document.removeEventListener('selectionchange', this.syncCursorFromTextarea)
            document.removeEventListener(SELECT_ALL_TEXT_EVENT, this.handleSelectAllTextEvent)
        }
        document.removeEventListener('mousemove', this.handleTextMouseMove)
        document.removeEventListener('mouseup', this.handleTextMouseUp)
    }

    getIsActive = () => {
        //do not use this.props.activeShapeId, not working, but needed for component did update!
        const ss = store.getState() as StateType
        return ss.activeShapeId && this.props.shape.index === ss.activeShapeId
    }

    handleSelectAllTextEvent = (event: Event) => {
        const { shape } = this.props

        const index = (event as CustomEvent).detail?.index
        const isActive = this.getIsActive()
        if (!isActive || index !== shape.index) {
            return
        }

        const length = this.props.shape.text_content ? this.props.shape.text_content.length : 0 //tady musi byt text content bez mezer!!!!!

        this.keySelectionAnchor = null

        this.setState({
            cursorPosition: length,
            textSelection: length ? { from: 0, to: length } : null,
        })
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps) {
            /* if (
                prevProps.shape.text_content !== this.props.shape.text_content
            ) {
                //tspan refs are only attached while active (see SVGTextWrapped), so ranges computed while inactive are stale
                this.updateTextRanges()
            } */

            if (
                (prevProps.activeShapeId !== this.props.activeShapeId) ||   //important for update on arrows move
                (prevProps.shape.index !== this.props.shape.index) ||
                (prevProps.shape.text_content !== this.props.shape.text_content) ||
                (prevProps.shape.text_fontsize !== this.props.shape.text_fontsize) ||
                (prevProps.shape.text_fontfamily !== this.props.shape.text_fontfamily) ||
                (prevProps.shape.text_stroke_color !== this.props.shape.text_stroke_color) ||
                (prevProps.shape.text_stroke_width !== this.props.shape.text_stroke_width) ||
                (prevProps.shape.text_stroke_underlayer !== this.props.shape.text_stroke_underlayer) ||
                (prevProps.shape.letter_case !== this.props.shape.letter_case) ||
                (prevProps.shape.letter_spacing !== this.props.shape.letter_spacing) ||
                (prevProps.shape.line_spacing !== this.props.shape.line_spacing) ||
                (prevProps.shape.text_padding !== this.props.shape.text_padding) ||
                (prevProps.shape.x !== this.props.shape.x) ||
                (prevProps.shape.y !== this.props.shape.y) ||
                (prevProps.shape.width !== this.props.shape.width) ||
                (prevProps.shape.height !== this.props.shape.height)
            ) {
                this.updateTextRangesDebounced()
                this.checkOverflowDebounced()
            }
        }
    }

    //keeps the cursor within the text bounds - callers (arrow keys, backspace at position 0, ...) can otherwise
    //push it negative, and String.prototype.substring silently clamps negative indexes to 0, which reorders
    //characters typed afterwards instead of just failing to move
    clampCursorPosition = (position: number): number => {
        const length = this.props.shape.text_content ? this.props.shape.text_content.length : 0
        return max(0, min(position, length))
    }

    setCursorPosition = (newCursorPosition: number) => {
        this.setState({cursorPosition: this.clampCursorPosition(newCursorPosition)})
    }

    /**
     * moves the cursor, optionally extending the current text selection (shift+arrow-style movement)
     */
    moveCursorTo = (newCursorPosition: number, extendSelection: boolean) => {
        newCursorPosition = this.clampCursorPosition(newCursorPosition)
        //console.debug(`inside moveCursorTo ${newCursorPosition}, extendSelection ${extendSelection}`)
        if (extendSelection) {
            if (this.keySelectionAnchor === null) {
                this.keySelectionAnchor = this.state.cursorPosition
            }
            const anchor = this.keySelectionAnchor
            this.setState({
                cursorPosition: newCursorPosition,
                textSelection: newCursorPosition === anchor ? null : { from: min(anchor, newCursorPosition), to: max(anchor, newCursorPosition) },
            })
        } else {
            this.keySelectionAnchor = null
            this.setState({
                cursorPosition: newCursorPosition,
                textSelection: null
            })
        }
    }

    checkOverflowDebounced = debounce(() => {
        this.checkOverflow()
    }, 200)

    checkOverflow = () => {
        const { shape } = this.props
        const textBox = this.mainText?.current ? this.mainText.current.getBBox() : null
        if (textBox) {
            checkOverflowAndResizeShape(shape, { width: round(textBox.width), height: round(textBox.height) }, this.props.updateShape)
        }
    }

    handleTextClick = (e: React.MouseEvent<SVGTextElement | SVGTSpanElement>, index: number): void => {
        if (this.mouseDownWasDrag) {   //a drag-selection just finished, ignore the trailing click
            this.mouseDownWasDrag = false
            return
        }

        if (e.detail >= 2) {   //double (or triple) click - shape.tsx's selectShape dispatches SELECT_ALL_TEXT_EVENT for this click, don't clobber that selection
            return
        }

        if (this.props.editMode === 'text' && (e.target instanceof SVGTSpanElement || e.target instanceof SVGTextElement)) {
            const j = this.getClickCursorPosition(e, index)
            this.keySelectionAnchor = null
            this.setState({
                cursorPosition: j,
                textSelection: null
            })
        }
    }

    handleTextMouseDown = (e: React.MouseEvent<SVGTextElement | SVGTSpanElement>, index: number): void => {
        if (this.props.editMode !== 'text' || !(e.target instanceof SVGTSpanElement || e.target instanceof SVGTextElement)) {
            return
        }

        const j = this.getCursorPositionForElement(this.getLineElement(e.target, index), e.clientX, index)

        this.selectionAnchorPos = j
        this.mouseDownWasDrag = false
        this.keySelectionAnchor = null

        this.setState({ cursorPosition: j, textSelection: null, isSelecting: true })

        document.addEventListener('mousemove', this.handleTextMouseMove)
        document.addEventListener('mouseup', this.handleTextMouseUp)
    }

    handleTextMouseMove = (e: MouseEvent): void => {
        if (!this.state.isSelecting) {
            return
        }

        const { el, index } = this.resolveLineElementAndIndex(e.target, e.clientY)
        if (!el) {
            return
        }

        const j = this.getCursorPositionForElement(el, e.clientX, index)

        if (j !== this.selectionAnchorPos) {
            this.mouseDownWasDrag = true
        }

        const from = min(this.selectionAnchorPos, j)
        const to = max(this.selectionAnchorPos, j)

        this.setState({
            cursorPosition: j,
            textSelection: from === to ? null : { from, to },
        })
    }

    handleTextMouseUp = (): void => {
        document.removeEventListener('mousemove', this.handleTextMouseMove)
        document.removeEventListener('mouseup', this.handleTextMouseUp)
        this.setState({ isSelecting: false })
    }

    /**
     * finds the tspan/text line under the given viewport y-coordinate, clamping to the first/last line
     * when the pointer moved outside the text vertically (drag-select past the edges)
     */
    resolveLineElementAndIndex = (target: EventTarget | null, clientY: number): { el: SVGTextElement | SVGTSpanElement | null, index: number } => {
        if (this.tspanList && this.tspanList.length) {
            const idx = this.tspanList.findIndex(ref => ref.current === target)
            if (idx !== -1) {
                return { el: this.tspanList[idx].current, index: idx }
            }

            let nearestIndex = 0
            let nearestDist = Infinity
            this.tspanList.forEach((ref, i) => {
                if (!ref.current) {
                    return
                }
                const box = ref.current.getBoundingClientRect()
                const center = box.top + .5 * box.height
                const dist = abs(clientY - center)
                if (dist < nearestDist) {
                    nearestDist = dist
                    nearestIndex = i
                }
            })

            return { el: this.tspanList[nearestIndex]?.current || null, index: nearestIndex }
        }

        return { el: this.mainText?.current || null, index: 0 }
    }

    getClickCursorPosition = (e, line: number): number => {
        return this.getCursorPositionForElement(this.getLineElement(e.target, line), e.clientX, line)
    }

    /**
     * pango markup: the click target is a styled run <tspan> inside the line, positions are counted in the whole line
     */
    getLineElement = (target: any, line: number): any => {
        if (!hasPangoMarkup(this.props.shape.text_content)) {
            return target
        }

        const lineEl = this.tspanList && this.tspanList[line] ? this.tspanList[line].current : null
        return lineEl || this.mainText?.current || target
    }

    /**
     * pango markup: [x, y] in the rendered plain text lines -> position in the text_content with the tags
     */
    markupPositionToScalar = (x: number, y: number): number => {
        const markup = this.props.shape.text_content
        const tspans = (this.tspanList || []).map(ref => ref.current).filter(el => !!el)
        const lineElements = tspans.length ? tspans : [this.mainText?.current]
        const ranges = getPangoLineRanges(stripPangoMarkup(markup), lineElements.map(el => el && el.textContent ? el.textContent : ''))

        return getPangoMarkupIndex(markup, (ranges[y] ? ranges[y].from : 0) + x)
    }

    getCursorPositionForElement = (el: any, clientX: number, line: number): number => {
        const textContentLength = el.textContent.length

        const yposition = line >= 0 ? line : 0

        const isMarkup = hasPangoMarkup(this.props.shape.text_content)

        if (!textContentLength) {
            return isMarkup ? this.markupPositionToScalar(0, yposition) : this.positionToScalar(0, yposition, false)
        }

        const firstCharPositionStart = el.getStartPositionOfChar(0)
        const firstCharPositionEnd = el.getEndPositionOfChar(0)

        const lastCharPositionStart = el.getStartPositionOfChar(textContentLength - 1)
        const lastCharPositionEnd = el.getEndPositionOfChar(textContentLength - 1)

        const firstCharMiddle = round(.5 * (firstCharPositionStart.x + firstCharPositionEnd.x))
        const lastCharMiddle = round(.5 * (lastCharPositionStart.x + lastCharPositionEnd.x))

        const box = el.getBoundingClientRect()
        const bbox = el.getBBox()

        const dx = round(bbox.width * (clientX - box.left) / box.width - .5 * bbox.width)

        let xposition = 0

        if (dx <= firstCharMiddle) {
            xposition = 0
        } else if (dx > lastCharMiddle) {
            xposition = textContentLength
        } else {
            let nearestX = firstCharPositionStart.x
            for (let i = 0; i < textContentLength; i++) {
                const start = el.getStartPositionOfChar(i)
                const end = el.getEndPositionOfChar(i)
                const middle = round(.5 * (start.x + end.x))
                if (abs(nearestX - dx) >= abs(middle - dx)) {
                    nearestX = middle
                    xposition = i
                }
            }
        }

        const j = isMarkup ? this.markupPositionToScalar(xposition, yposition) : this.positionToScalar(xposition, yposition, false)
        //console.debug(`CLICK ON [${xposition}, ${yposition}] -------> ${j}, char: ${this.props.shape.text_content.substring(j, j + 4)}`)

        return j
    }

    positionToScalar = (x: number, y: number, includeLeadingSpaces: boolean): number => {
        const {textRanges, textRangesWithLeadingSpaces} = this.state

        let addX = 0
        if (includeLeadingSpaces && typeof textRangesWithLeadingSpaces !== 'undefined' && typeof textRangesWithLeadingSpaces[y] !== 'undefined') {
            addX = textRangesWithLeadingSpaces[y].from - textRanges[y].from
        }

        return (textRanges && typeof textRanges[y] !== 'undefined' && typeof textRanges[y].from !== 'undefined' ? textRanges[y].from : 0) + x - addX
    }

    scalarToPosition = (textContentPos: number, outputWithoutLeadingSpaces: boolean): { x: number, y: number } => {
        const { textRanges, textRangesWithLeadingSpaces } = this.state
        const { shape } = this.props

        const content = getTrimmedContent(shape)
        const textContentLength = content?.length || 0

        let x = 0
        let y = 0

        if (textContentLength && textContentPos) {
            if (textRanges.length <= 1) {
                return { x: textContentPos, y: 0 }    //single line
            }

            textRanges.forEach((range, j) => {
                if (textContentPos >= range.from) {
                    const addX = outputWithoutLeadingSpaces ? (textRangesWithLeadingSpaces[j].from - range.from) : 0
                    x = textContentPos - range.from + addX * 0
                    y = j
                }
            })
        }

        return { x, y }
    }

    /**
    * each lines position in the real text_content
    */
    getTextRanges = (includingWhiteSpaces: boolean, includeLeadingSpaces: boolean): RangeType[] => {
        const { shape } = this.props

        if (!this.tspanList || !this.tspanList.length || !shape.text_content) {
            return []
        }

        let startIndexes = []
        let endIndexes = []

        let pointer = 0 //if not includingWhiteSpace then the last pointer position in the real textContent
        let prevLength = 0

        this.tspanList.forEach((ref, i: number) => {
        //shape.text_content.split("\n").forEach((lineText, i: number) => {
            //const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
            //tspan.innerHTML = lineText
            //console.debug(`debug tspan: *${tspan.textContent}* linetext: *${lineText}*`)

            //const el = ref.current
            //if (el) {

            const lineText = ref.current?.textContent
            if (typeof lineText === 'string') {
                if (includingWhiteSpaces) {
                    //word-wrapping collapses runs of whitespace to a single space (see getAdaptiveFontSize), so lineText
                    //is not necessarily a verbatim substring of shape.text_content when the real text has multiple spaces -
                    //match word-by-word allowing any-length whitespace between them to find the real start/length
                    const words = lineText.split(' ').filter(w => w.length)

                    let from: number
                    let matchedLength: number

                    if (words.length) {
                        const pattern = words.map(escapeRegExp).join('\\s+')
                        const match = new RegExp(pattern).exec(shape.text_content.substring(pointer))
                        from = match ? match.index + pointer : shape.text_content.substring(pointer).indexOf(lineText) + pointer
                        matchedLength = match ? match[0].length : lineText.length
                    } else {
                        //a blank line (consecutive manual line breaks) has no word to anchor a search on - it sits
                        //right after the line break that ended the previous line
                        from = shape.text_content[pointer] === manualBreakLine ? pointer + 1 : pointer
                        matchedLength = 0
                    }

                    //console.debug(`looking for *${lineText}* found at ${from}, shape text content is`, shape.text_content)

                    if (i > 0) {
                        const remainder = shape.text_content.substring(startIndexes[i - 1], from)
                        const p = remainder.indexOf("\n")
                        const to = p === -1 ? from : startIndexes[i - 1] + p
                        endIndexes[i - 1] = to
                    }
                    startIndexes[i] = from
                    pointer = from + matchedLength
                } else {
                    startIndexes[i] = pointer
                    if (i > 0) {
                        endIndexes[i - 1] = prevLength
                    }
                    prevLength = lineText.length + 1   //???test this
                    pointer += lineText.length + 1
                }
                //}
            }
        })

        const ranges: RangeType[] = startIndexes.map((startIndex, j) => {
            return {
                from: startIndex,
                to: endIndexes[j] ?? undefined,
            }
        })

        return ranges
    }

    //mirrors the shape's redux-form <textarea> cursor position onto the SVG cursor overlay - driven by the
    //document "selectionchange" event so it reflects the caret *after* the browser has moved it (a "click" or
    //"keydown" listener would fire too early for arrow/Home/End navigation, which updates selectionStart only
    //as part of the browser's default keydown handling)
    syncCursorFromTextarea = () => {
        const isActive = this.getIsActive()

        if (!isActive) {
            return
        }

        const ss = store.getState() as StateType
        const formName = getFormName(ss.isAlt)
        const id = `${formName}.shapes.${this.props.shape.index}.text_content`
        const textarea = document.getElementById(id) as any;

        //only trust the textarea's cursor while it's actually focused - otherwise (e.g. a click on the SVG canvas,
        //which can also trigger a "selectionchange") its stale selectionStart would clobber the position that
        //the canvas's own click handler just set
        if (textarea && document.activeElement === textarea) {
            const { selectionStart, selectionEnd } = textarea

            if (hasPangoMarkup(this.props.shape.text_content)) {
                //text_content positions are mapped onto the rendered plain text by TextCursorSvg
                this.keySelectionAnchor = null
                this.setState({ cursorPosition: selectionStart, textSelection: selectionEnd > selectionStart ? { from: selectionStart, to: selectionEnd } : null })
                return
            }

            const startPos = this.scalarToPosition(selectionStart, false)
            const cursorPosition = this.positionToScalar(startPos.x, startPos.y, false)

            let textSelection: RangeType = null
            if (selectionEnd > selectionStart) {
                const endPos = this.scalarToPosition(selectionEnd, false)
                const to = this.positionToScalar(endPos.x, endPos.y, false)
                textSelection = { from: cursorPosition, to }
            }

            this.keySelectionAnchor = null
            this.setState({ cursorPosition, textSelection })
        }
    }

    handleKeyDown = (event) => {
        const { shape, editMode } = this.props
        const { cursorPosition, textSelection } = this.state

        const ss = store.getState() as StateType
        const isLocked = ss.lockedLayers && ss.lockedLayers.includes(shape.index)
        const isActive = shape.index === ss.activeShapeId

        //pango markup: typing/moving by one char would step into the tags, edit it in the form only
        if (['TEXTAREA', 'INPUT'].includes(event.target.tagName) || !isActive || editMode !== 'text' || isLocked || hasPangoMarkup(shape.text_content)) {
            return false
        }

        //console.debug(`handle key down ${event.key}, edit mode is ${editMode}`, event.target.tagName)

        const textContent = shape.text_content
        const hasSelection = !!textSelection

        const NAVIGATION_KEYS = [RIGHT_ARROW_KEY, LEFT_ARROW_KEY, DOWN_ARROW_KEY, UP_ARROW_KEY, PAGE_UP_KEY, PAGE_DOWN_KEY, HOME_KEY, END_KEY]
        const FORWARD_NAVIGATION_KEYS = [RIGHT_ARROW_KEY, DOWN_ARROW_KEY, PAGE_DOWN_KEY, END_KEY]

        //when a selection exists and the user navigates without extending it, collapse to the edge in the direction of movement (standard text-editor behavior)
        const effectiveCursorPosition = hasSelection && !event.shiftKey && NAVIGATION_KEYS.includes(event.keyCode)
            ? (FORWARD_NAVIGATION_KEYS.includes(event.keyCode) ? textSelection.to : textSelection.from)
            : cursorPosition

        //const lines = getChildNodesTexts(true, true)
        const pos = this.scalarToPosition(effectiveCursorPosition, false)
        const maxCursorPositionY = this.tspanList?.length ?? 0 - 1

        switch (event.keyCode) {
            case RIGHT_ARROW_KEY:
                event.preventDefault()
                this.moveCursorTo(effectiveCursorPosition + 1, event.shiftKey)
                break
            case LEFT_ARROW_KEY:
                event.preventDefault()
                this.moveCursorTo(effectiveCursorPosition - 1, event.shiftKey)
                break
            case DOWN_ARROW_KEY:
                const newY = pos.y + 1
                if (newY <= maxCursorPositionY) {
                    const maxCursorPositionX = typeof this.tspanList[newY] !== 'undefined' ? this.tspanList[newY].current.textContent.length : 0  //one position after last letter
                    const newCursporPositionX = pos.x <= maxCursorPositionX ? pos.x : maxCursorPositionX
                    this.moveCursorTo(this.positionToScalar(newCursporPositionX, newY, false), event.shiftKey)
                }
                break
            case UP_ARROW_KEY:
                event.preventDefault()
                const newY2 = pos.y - 1
                if (newY2 >= 0) {
                    const maxCursorPositionX = typeof this.tspanList[newY2] !== 'undefined' ? this.tspanList[newY2].current.textContent.length : 0
                    const newCursporPositionX = pos.x <= maxCursorPositionX ? pos.x : maxCursorPositionX
                    this.moveCursorTo(this.positionToScalar(newCursporPositionX, newY2, false), event.shiftKey)
                }
                break
            case PAGE_UP_KEY:
                event.preventDefault()
                const maxX = this.tspanList[0].current.textContent.length - 1
                this.moveCursorTo(this.positionToScalar(pos.x <= maxX ? pos.x : maxX, 0, false), event.shiftKey)
                break
            case PAGE_DOWN_KEY:
                event.preventDefault()
                const newYDown = this.tspanList.length - 1
                const maxCursorPositionX = this.tspanList[newYDown].current.textContent.length - 1
                this.moveCursorTo(this.positionToScalar(pos.x <= maxCursorPositionX ? pos.x : maxCursorPositionX, maxCursorPositionY, false), event.shiftKey)
                break
            case END_KEY:
                event.preventDefault()
                this.moveCursorTo(textContent.length, event.shiftKey)
                break
            case HOME_KEY:
                event.preventDefault()
                this.moveCursorTo(0, event.shiftKey)
                break
            case BACKSCAPE_KEY:
                event.preventDefault()
                if (hasSelection) {
                    this.keySelectionAnchor = null
                    this.setState({ cursorPosition: textSelection.from, textSelection: null })
                    this.props.updateShape({
                        ...shape,
                        text_content: textContent.substring(0, textSelection.from) + textContent.substring(textSelection.to)
                    })
                } else {
                    this.setCursorPosition(cursorPosition - 1)
                    this.props.updateShape({
                        ...shape,
                        text_content: textContent.substring(0, cursorPosition - 1) + textContent.substring(cursorPosition)
                    })
                }
                break
            case DELETE_KEY:
                event.preventDefault()
                if (hasSelection) {
                    this.keySelectionAnchor = null
                    this.setState({
                        cursorPosition: textSelection.from,
                        textSelection: null
                    })
                    this.props.updateShape({
                        ...shape,
                        text_content: textContent.substring(0, textSelection.from) + textContent.substring(textSelection.to)
                    })
                } else {
                    this.props.updateShape({
                        ...shape,
                        text_content: textContent.substring(0, cursorPosition) + textContent.substring(cursorPosition + 1)
                    })
                }
                break
            default:
                if (!event.ctrlKey) {
                    const isNewLine = event.keyCode === ENTER_KEY
                    if ((event.key && event.key.length === 1 || isNewLine)) {
                        event.preventDefault()
                        const char = isNewLine ? "\n" : event.key

                        const rangeFrom = hasSelection ? textSelection.from : cursorPosition
                        const rangeTo = hasSelection ? textSelection.to : cursorPosition

                        const newTextContent = textContent ? textContent.substring(0, rangeFrom) + char + textContent.substring(rangeTo) : char
                        const textContentReplaced = newTextContent ? newTextContent.replace(/[^\S\n]+/g, " ") : ''   //remove extra spaces (except the line breaks) in text (tspan ignores them anyway)

                        //TESTOVAT KEY V ROZSAHU A-Z,a-z,0-9,\t\w tisknutelne znaky a znaky ve fontu, dat tomu target elementu napr. data atribut???
                        this.keySelectionAnchor = null
                        this.setState({ cursorPosition: rangeFrom + 1, textSelection: null })
                        this.props.updateShape({
                            ...shape,
                            text_wrap: isNewLine ? 1 : shape.text_wrap,
                            text_content: textContentReplaced
                        })
                    } else {
                        console.debug(`unknown key ${event.key}, code ${event.keyCode}`)
                    }
                }
                break
        }

        return true
    }

    render(): JSX.Element {
        const { shape, width, h, fontSize, offsetY, editMode } = this.props

        const ss = store.getState() as StateType
        const isActive = ss.activeShapeId && ss.activeShapeId === shape.index
        const isLocked = ss.lockedLayers && ss.lockedLayers.includes(shape.index)
        const lines = typeof shape.lines === 'string' && isJson(shape.lines) ? JSON.parse(shape.lines) : shape.lines
        const trimmedContent = getTrimmedContent(shape)
        const isMarkup = hasPangoMarkup(trimmedContent)
        const allowCursor = isActive && !NO_TEXT_CURSOR_TYPES.includes(shape.type) && !isLocked
        const lineSpacing = shape.line_spacing ? shape.line_spacing : 0
        const textPadding = shape.text_padding ? shape.text_padding : 0
        const textAnchor: TextAnchorType = shape.text_align === TEXT_HORIZONTAL_ALIGN_RIGHT ? 'end' : (shape.text_align === TEXT_HORIZONTAL_ALIGN_LEFT ? 'start' : 'middle')
        const dominantBaseline = "central" //mathematical?
        const isVertical = shape.text_orientation === TEXT_ORIENTATION_VERTICAL
        const textDecorationColor = shape.text_decoration_color || shape.text_color || '#000'
        const textDecorationLineThickness = shape.text_decoration_linethickness || ceil(shape.text_fontsize / 20)
        const textDecorationXLineOverlap = shape.text_decoration_lineoverlap || 0
        const horizontalAlign = shape.text_align || 0
        const verticalAlign = shape.text_valign || 0

        const halfStrokeWidth = shape.stroke_width ? .5 * shape.stroke_width : 0

        const height = (h || width)

        const offsetX = .5 * (width - 2 * textPadding - halfStrokeWidth) * horizontalAlign

        const isShadow = shape.shadow_color && shape.shadow_sigma
        const filter = isShadow ? `url(#shadow-${shape.index})` : ''

        if (isVertical || !!shape.text_wrap) {
            return <React.Fragment>
                <SVGTextWrapped
                    dominantBaseline={dominantBaseline}
                    width={width}
                    height={height || width}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    filter={filter}
                    shape={shape}
                    updateShape={this.props.updateShape}
                    isActive={isActive}
                    setRealSize={this.props.setRealSize}
                    cursorPosition={this.state.cursorPosition}
                    textSelection={this.state.textSelection}
                    setCursorPosition={this.setCursorPosition}
                    handleTextClick={this.handleTextClick}
                    handleTextMouseDown={this.handleTextMouseDown}
                    mainText={this.mainText}
                    tspanList={this.tspanList}
                    textRanges={this.state.textRanges}
                    scalarToPosition={this.scalarToPosition}
                    editMode={editMode}
                    trimmedContent={trimmedContent}
                />
            </React.Fragment>
        }

        const adaptiveFontSizeAndStyle = getAdaptiveFontSize(shape, width, height, null)
        const size = getStringWidthAndHeight(trimmedContent, adaptiveFontSizeAndStyle.updatedStyle)

        const metrics = getFontMetrics(adaptiveFontSizeAndStyle.updatedStyle)
        //console.debug(`real fontsize ${fontSize}`, metrics)

        const debugMetrics = process.env.APP_ENV === 'local' && !!process.env.DEBUG_TEXT_METRICS   //only on localhost, mirrors TextRenderer.php's $debugMetrics

        const metricsHeight = fontSize  //keep as before until new fix
        //const metricsHeight = metrics ? metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent : fontSize
        // fontBoundingBoxAscent/Descent come from the font's own metrics table, which for some (especially
        // custom/uploaded) fonts underreports the actual glyph extents - fall back to the real rendered
        // bbox height (size.h) whenever it's taller than what the font claims.

        const fontHeight = size && size.h > metricsHeight ? size.h : metricsHeight
        const addOffsetY = .5 * (height - 2 * textPadding - fontHeight - halfStrokeWidth) * verticalAlign
        const decorationOffset = -.5 * size.h + (shape.text_decoration === TEXT_DECORATION_LINETHROUGH ? .5 * size.h : shape.text_decoration === TEXT_DECORATION_OVERLINE ? 0 : size.h)

        const x1 = -.5 * (horizontalAlign + 1) * size.w

        const bgcolor = shape.gradient_id && shape.gradient ? GradientFillUrl(shape.gradient_id) : (shape.text_background_color || '#transparent')
        const fillOpacity = shape.gradient_id && shape.gradient && shape.gradient.opacity ? shape.gradient.opacity / 100 : 1
        const cornerRadius = shape.corner_radius ?? null

        const isStrokeUnderlayer = !!shape.text_stroke_underlayer && !!shape.text_stroke_color && !!shape.text_stroke_width
        const topTextStyle = isStrokeUnderlayer ? { ...adaptiveFontSizeAndStyle.updatedStyle, stroke: undefined, strokeWidth: 0 } : adaptiveFontSizeAndStyle.updatedStyle
        const letterSpacingDx = shape.letter_spacing ? (textAnchor === 'middle' ? `${round(shape.letter_spacing/2)}px`: textAnchor === 'end' ? `${round(shape.letter_spacing)}px` : undefined): undefined //letter spacing leaves space after last char

        return <g transform={`translate(${offsetX}, ${offsetY + addOffsetY})`}>
            <rect
                className='move-box'
                x={-.5 * width}
                y={- .5 * height}
                height={height}
                width={width}
                fill={'transparent'}
                style={{
                    cursor: MOVE_CURSOR,
                }}
            />

            {shape.text_background_type === RECTANGLE_EACH_LINE_TYPE &&
                <rect
                    x={x1}
                    y={- .5 * size.h}
                    height={size.h}
                    width={size.w}
                    fill={bgcolor}
                    fillOpacity={fillOpacity}
                    strokeWidth={shape.stroke_width}
                    stroke={shape.stroke_color}
                    filter={filter}
                    rx={cornerRadius ? cornerRadius : undefined}
                    ry={cornerRadius ? cornerRadius : undefined}
                />
            }

            {debugMetrics && metrics &&
                <React.Fragment>
                    <rect
                        x={x1}
                        y={-.5 * size.h}
                        width={size.w}
                        height={size.h}
                        fill="#0000ff"
                        fillOpacity={0.02}
                        stroke="#000000"
                        strokeWidth={1}
                    />
                    <rect
                        x={x1 - 50}
                        y={-.5 * metricsHeight}
                        width={size.w + 100}
                        height={metricsHeight}
                        fill="#ff0000"
                        fillOpacity={0.055}
                        stroke="#ffd901"
                        strokeWidth={1}
                    />
                    <line
                        x1={x1 - 50}
                        x2={x1 + size.w + 50}
                        y1={-.5 * metricsHeight}
                        y2={-.5 * metricsHeight}
                        stroke="#ff0000"
                        strokeWidth={1}
                    />
                    <line
                        x1={x1 - 50}
                        x2={x1 + size.w + 50}
                        y1={.5 * metricsHeight}
                        y2={.5 * metricsHeight}
                        stroke="#00ff00"
                        strokeWidth={1}
                    />
                    <line
                        x1={x1 - 50}
                        x2={x1 + size.w + 50}
                        y1={.5 * (metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent)}
                        y2={.5 * (metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent)}
                        stroke="#0000ff"
                        strokeWidth={1}
                    />
                </React.Fragment>
            }

            {isStrokeUnderlayer &&
                <g filter={filter}>
                    <StrokeUnderlayerFilter
                        id={this.underlayerFilterId}
                        strokeWidth={shape.text_stroke_width}
                        box={{ x: 0, y: 0, width, height }}
                    />
                    <text
                        dominantBaseline={dominantBaseline}
                        textAnchor={textAnchor}
                        fill={shape.text_stroke_color}
                        style={{ ...adaptiveFontSizeAndStyle.updatedStyle, pointerEvents: 'none' }}
                        dx={letterSpacingDx}
                        filter={strokeUnderlayerFilterUrl(this.underlayerFilterId, shape.text_stroke_width)}
                    >{isMarkup ? renderPangoMarkup(trimmedContent, 'underlayer', false) : trimmedContent}
                    </text>
                </g>
            }

            <text
                ref={this.mainText}
                className={allowCursor ? `editable-text${isActive ? '-active' : ''}` : undefined}
                dominantBaseline={dominantBaseline}
                textAnchor={textAnchor}
                fill={shape.text_color}
                style={{ ...topTextStyle, pointerEvents: 'auto', cursor: allowCursor ? 'text' : undefined }}
                dx={letterSpacingDx}
                filter={isStrokeUnderlayer ? undefined : filter}
                onClick={(e) => this.handleTextClick(e, 0)}
                onMouseDown={(e) => this.handleTextMouseDown(e, 0)}
            >{isMarkup ? renderPangoMarkup(trimmedContent, 'text') : trimmedContent}
            </text>

            {shape.text_decoration && shape.text_decoration !== TEXT_DECORATION_NONE &&
                <line
                    x1={`${x1 - textDecorationXLineOverlap}`}
                    x2={`${x1 + size.w + textDecorationXLineOverlap}`}
                    y1={`${decorationOffset}`}
                    y2={`${decorationOffset}`}
                    strokeWidth={textDecorationLineThickness}
                    stroke={textDecorationColor}
                />
            }

            {allowCursor && editMode === 'text' &&
                <TextCursorSvg
                    h={adaptiveFontSizeAndStyle.fontSize}
                    mainText={this.mainText}
                    tspanList={null}
                    textContent={shape.text_content}
                    cursorPosition={this.state.cursorPosition}
                    textSelection={this.state.textSelection}
                    textRanges={this.state.textRanges}
                    setCursorPosition={this.setCursorPosition}
                    shape={shape}
                    updateShape={this.props.updateShape}
                    scalarToPosition={this.scalarToPosition}
                />
            }
        </g>
    }
}

/**
 * Gets Real Size of an existing element.
 */
export function getRealSizeByElementId(elementId: string, width: number = 0, height: number = 0, paddingCoef: number = 1): { realWidth: number, realHeight: number } {
    const el = document.getElementById(elementId)

    if (!el) {
        return { realWidth: width, realHeight: height }
    }

    const textElement = el.querySelector('text')

    if (!textElement) {
        //console.debug(`no text element, we have this element for ${elementId} from params ${width}x${height}`, el)
        return { realWidth: width, realHeight: height }
    }

    const box = textElement.getBBox()

    const realWidth = box.width
    const realHeight = box.height || box.width

    return {
        realWidth: round(paddingCoef * realWidth),
        realHeight: round(paddingCoef * realHeight)
    }
}


export const DEFAULT_FONT_WEIGHT = 300
export const DEFAULT_FONT_NAME = 'Roboto'

export const OVERFLOW_KEEP_AS_IS = 0
export const OVERFLOW_FONTSIZE = 1
export const OVERFLOW_LAYERSIZE = 2

export const TEXT_DECORATION_NONE = 1
export const TEXT_DECORATION_UNDERLINE = 2
export const TEXT_DECORATION_OVERLINE = 3
export const TEXT_DECORATION_LINETHROUGH = 4

export const TEXT_HORIZONTAL_ALIGN_LEFT = -1
export const TEXT_HORIZONTAL_ALIGN_CENTER = 0
export const TEXT_HORIZONTAL_ALIGN_RIGHT = 1

export const TEXT_VERTICAL_ALIGN_START = -1
export const TEXT_VERTICAL_ALIGN_MIDDLE = 0
export const TEXT_VERTICAL_ALIGN_END = 1

export const TEXT_DIRECTION_CLOCKWISE = 0
export const TEXT_DIRECTION_COUNTER_CLOCKWISE = 1

export const TEXT_ORIENTATION_HORIZONTAL = 0
export const TEXT_ORIENTATION_VERTICAL = 1

export const LETTER_CASE_KEEP = 0
export const LETTER_CASE_TO_UPPER = 1
export const LETTER_CASE_TO_LOWER = 2
export const LETTER_CASE_FIRST_LETTER_TO_UPPER = 3
export const LETTER_CASE_FIRST_LETTER_TO_LOWER = 4
export const LETTER_CASE_WORDS_FIRST_TO_UPPER = 5

export default RenderText